import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Add, Edit, Delete, Download as DownloadIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import TableComponent from "../../components/Table";
import ConfirmDialog from "../../components/ConfirmDialog";
import theme from "../../theme/theme";
import {
  fetchItems,
  createItem,
  updateItem,
  deleteItem,
  fetchAllItemCategories,
  exportItems,
} from "../../api/InventoryService";

const ItemMasterPage = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination & Filtering
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [links, setLinks] = useState([]);
  const [meta, setMeta] = useState({ from: 0, to: 0, total: 0 });
  const [filters, setFilters] = useState({ search: "", category_id: "" });
  const [exporting, setExporting] = useState(false);

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    sku: "",
    description: "",
    category_id: "",
    isSkuEdited: false,
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadDependencies = async () => {
    try {
      const res = await fetchAllItemCategories();
      setCategories(res.data || []);
    } catch (e) {
      toast.error("Failed to load categories");
    }
  };

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchItems({ page: currentPage, per_page: perPage, ...filters });
      setItems(res.data.data || []);
      setLinks(res.data.links || []);
      setMeta({ from: res.data.from, to: res.data.to, total: res.data.total });
    } catch (error) {
      toast.error("Failed to fetch items");
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, filters]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportItems(filters);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "items.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error("Failed to export items");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    loadDependencies();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [perPage]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setFormData({
        id: item.id,
        name: item.name,
        sku: item.sku,
        description: item.description || "",
        category_id: item.category_id,
        isSkuEdited: true, // Existing items already have SKUs, don't auto-generate
      });
    } else {
      setFormData({
        id: null,
        name: "",
        sku: "",
        description: "",
        category_id: "",
        isSkuEdited: false,
      });
    }
    setOpenDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.sku || !formData.category_id) {
      return toast.warning("Name, SKU, and Category are required");
    }
    setSaving(true);
    try {
      if (formData.id) {
        await updateItem(formData.id, formData);
        toast.success("Item updated");
      } else {
        await createItem(formData);
        toast.success("Item created");
      }
      setOpenDialog(false);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteTarget(item);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteItem(deleteTarget.id);
      toast.success("Item deleted");
      setConfirmOpen(false);
      setDeleteTarget(null);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete item");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { field: "sku", headerName: "SKU" },
    { field: "name", headerName: "Item Name" },
    {
      field: "category.name",
      headerName: "Category",
      // render: (row) => row.category?.name || "N/A",
    },
    { field: "description", headerName: "Description" },
  ];

  const actions = [
    {
      icon: <Edit />,
      label: "Edit",
      onClick: (row) => handleOpenDialog(row),
      backgroundColor: theme.button.editBackgroundColor,
    },
    {
      icon: <Delete />,
      label: "Delete",
      onClick: (row) => handleDeleteClick(row),
      backgroundColor: theme.button.deleteBackgroundColor,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Item Master Catalog
        </Box>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{ backgroundColor: "#fff" }}
          >
            {exporting ? "Exporting..." : "Export Excel"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{ borderRadius: theme.button.borderRadius }}
          >
            Add Item
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          label="Search by Name or SKU"
          size="small"
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          sx={{ width: 300, backgroundColor: "#fff" }}
        />
        <FormControl
          size="small"
          sx={{ minWidth: 200, backgroundColor: "#fff" }}
        >
          <InputLabel>Category</InputLabel>
          <Select
            label="Category"
            value={filters.category_id}
            onChange={(e) =>
              setFilters({ ...filters, category_id: e.target.value })
            }
          >
            <MenuItem value="">
              <em>All Categories</em>
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          onClick={() => {
            setFilters({ search: "", category_id: "" });
            setCurrentPage(1);
          }}
        >
          Clear Filters
        </Button>
      </Box>

      <TableComponent
        columns={columns}
        data={items}
        actions={actions}
        loading={loading}
        links={links}
        setCurrentPage={setCurrentPage}
        meta={meta}
        perPage={perPage}
        setPerPage={setPerPage}
      />

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{formData.id ? "Edit Item" : "Add Item"}</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}
        >
          <FormControl fullWidth required>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={formData.category_id}
              onChange={(e) =>
                setFormData({ ...formData, category_id: e.target.value })
              }
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              required
              fullWidth
              label="SKU"
              value={formData.sku}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  sku: e.target.value.toUpperCase().replace(/\s+/g, "-"),
                  isSkuEdited: true,
                })
              }
            />
            <TextField
              required
              fullWidth
              label="Item Name"
              value={formData.name}
              onChange={(e) => {
                const newName = e.target.value;
                const newSku = formData.isSkuEdited
                  ? formData.sku
                  : newName.toUpperCase().replace(/\s+/g, "-");
                setFormData({ ...formData, name: newName, sku: newSku });
              }}
            />
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Item"
        message={`Are you sure you want to delete item "${deleteTarget?.name}"?`}
        loading={deleting}
      />
    </Box>
  );
};

export default ItemMasterPage;
